// ---------------------------------------------------------------------------
/**
 * Which recorded fields a history dialog should NOT list.
 *
 * `historyFields` is the SERVER's snapshot list — it decides what gets written
 * to the history table, so nothing may be dropped there just because it reads
 * badly in a dialog. The filtering belongs here, on the display side.
 *
 * What gets hidden are the plumbing fields: the `*Id` app-user links that are
 * resolved from a name on save (the NAME is in the history right beside them),
 * the reference arrays that would diff as bare UUIDs, the owning ids, and the
 * sketch's north-arrow geometry. None of them has a label in the catalogs
 * either — which is what made every open history dialog log a
 * MISSING_TRANSLATION per field.
 */
// ---------------------------------------------------------------------------

/** Owning references — they identify the record, they are not its content. */
const OWNER_FIELDS = ['projectId', 'findingId'];

/** The Planum's north arrow: geometry, not documentation. */
const NORTH_FIELDS = ['northArrow', 'northAngle', 'northX', 'northY'];

/**
 * True for a field the dialog should skip.
 *
 * `keep` is for the id fields a dialog DOES render, because it resolves them to
 * something readable — the project's `userIds`/`groupIds` become user and group
 * names, so they stay.
 */
export function isInternalHistoryField(
    key: string,
    keep: readonly string[] = []
): boolean {
    if (keep.includes(key)) {
        return false;
    }
    if (OWNER_FIELDS.includes(key) || NORTH_FIELDS.includes(key)) {
        return true;
    }
    // `editorId`, `excavatorId`, … and `planumIds`, `identicalWithPlanumIds`, …
    return /Ids?$/.test(key);
}

/** The recorded fields worth showing, in their recorded order. */
export function visibleHistoryFields<Key extends string>(
    fields: readonly Key[],
    keep: readonly string[] = []
): Key[] {
    return fields.filter((key) => !isInternalHistoryField(key, keep));
}
